import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';

import AppLayout from '@/components/blocks/layout/app-layout';
import Centered from '@/components/blocks/layout/centered';
import type { AuthContextProps } from '@/providers/auth-provider';
import Datasets from '@/routes/(app)/(datasets)/datasets';
import Thread from '@/routes/(app)/(threads)/thread';
import Threads from '@/routes/(app)/(threads)/threads';
import { Tracing } from '@/routes/(app)/(tracing)/tracing';
import DataAgent from '@/routes/(app)/agents/data-agent';
import FeedbackAgent from '@/routes/(app)/agents/feedback-agent';
import Login from '@/routes/(auth)/login';
import Root from '@/routes/root';

import './i18n/config';

interface RouterContext {
  auth?: AuthContextProps;
  queryClient?: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({ to: '/app/datasets' });
    } else {
      throw redirect({ to: '/auth/login' });
    }
  },
});

const authRoute = createRoute({
  path: 'auth',
  getParentRoute: () => rootRoute,
  component: () => (
    <Centered>
      <Outlet />
    </Centered>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'login',
  validateSearch: z.object({
    redirect: z.string().optional().catch(''),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({ to: search.redirect ?? '/app/datasets' });
    }
  },
  component: Login,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'app',
  beforeLoad: ({ context, location }) => {
    if (!context.auth?.isAuthenticated) {
      throw redirect({
        to: '/auth/login',
        search: {
          redirect: location.href,
        },
      });
    }
  },
  component: () => (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ),
});

const datasetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'datasets',
  component: Datasets,
});

const agentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'agents',
});

const dataAgentRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: 'data-agent',
  component: DataAgent,
});

const feedbackAgentRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: 'feedback-agent',
  component: FeedbackAgent,
});

const threadsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'threads',
  component: Threads,
});

export const threadRoute = createRoute({
  getParentRoute: () => threadsRoute,
  path: '$threadId',
  component: Thread,
});

const tracingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'tracing/$componentName',
  component: Tracing,
});

export const routes = rootRoute.addChildren([
  indexRoute,
  authRoute.addChildren([loginRoute]),
  appRoute.addChildren([
    datasetsRoute,
    agentsRoute.addChildren([dataAgentRoute, feedbackAgentRoute]),
    threadsRoute.addChildren([threadRoute]),
    tracingRoute,
  ]),
]);
